import FontFamily from "@tiptap/extension-font-family"
import Link from "@tiptap/extension-link"
import Table from "@tiptap/extension-table"
import TableCell from "@tiptap/extension-table-cell"
import TableHeader from "@tiptap/extension-table-header"
import TableRow from "@tiptap/extension-table-row"
import TextAlign from "@tiptap/extension-text-align"
import TextStyle from "@tiptap/extension-text-style"
import Underline from "@tiptap/extension-underline"
import { EditorContent, Extension, useEditor } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold as BoldIcon,
  Braces,
  ChevronDown,
  Code2,
  Indent,
  Italic as ItalicIcon,
  Link as LinkIcon,
  List,
  ListOrdered,
  Maximize2,
  Minimize2,
  Outdent,
  Redo2,
  RemoveFormatting,
  Strikethrough,
  Table as TableIcon,
  Underline as UnderlineIcon,
  Undo2,
} from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { SHARE_TEMPLATE_TOKENS } from "../../utils/variables/share_templates"

// ---- custom marks/extensions (not built into Tiptap core) ----------------

const FontSize = Extension.create({
  name: "fontSize",
  addOptions() {
    return { types: ["textStyle"] }
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (el) => el.style.fontSize || null,
            renderHTML: (attrs) =>
              attrs.fontSize ? { style: `font-size: ${attrs.fontSize}` } : {},
          },
        },
      },
    ]
  },
  addCommands() {
    return {
      setFontSize:
        (size) =>
        ({ chain }) =>
          chain().setMark("textStyle", { fontSize: size }).run(),
      unsetFontSize:
        () =>
        ({ chain }) =>
          chain().setMark("textStyle", { fontSize: null }).run(),
    }
  },
})

const LineHeight = Extension.create({
  name: "lineHeight",
  addOptions() {
    return { types: ["paragraph", "heading"] }
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          lineHeight: {
            default: null,
            parseHTML: (el) => el.style.lineHeight || null,
            renderHTML: (attrs) =>
              attrs.lineHeight ? { style: `line-height: ${attrs.lineHeight}` } : {},
          },
        },
      },
    ]
  },
  addCommands() {
    return {
      setLineHeight:
        (value) =>
        ({ commands }) =>
          this.options.types.every((type) =>
            commands.updateAttributes(type, { lineHeight: value })
          ),
    }
  },
})

// {{variable}} tokens get a visual chip via a mark on the inserted text run
const VariableMark = Extension.create({
  name: "variableMarkHelper",
  // no-op holder; token text is inserted with the "variableToken" mark below
})

// ---- static config ---------------------------------------------------

// SHARE_TEMPLATE_TOKENS: [{ key, label, sample? }, ...] — single source of truth
const VARIABLES = SHARE_TEMPLATE_TOKENS.map((tok) => tok.key)
const TOKEN_LABELS = Object.fromEntries(SHARE_TEMPLATE_TOKENS.map((tok) => [tok.key, tok.label]))
const SAMPLE_VALUES = Object.fromEntries(
  SHARE_TEMPLATE_TOKENS.map((tok) => [tok.key, tok.sample ?? tok.label ?? tok.key])
)

const FONT_FAMILIES = [
  "Inter",
  "Helvetica",
  "Arial",
  "Georgia",
  "Times New Roman",
  "Courier New",
  "Verdana",
]
const FONT_SIZES = ["11px", "12px", "13px", "14px", "16px", "18px", "20px", "24px"]
const LINE_HEIGHTS = ["1", "1.15", "1.5", "2"]

const HEADINGS = [
  { label: "saved_tours.share.editor.text", node: "paragraph", level: null },
  { label: "saved_tours.share.editor.h1", node: "heading", level: 1 },
  { label: "saved_tours.share.editor.h2", node: "heading", level: 2 },
  { label: "saved_tours.share.editor.h3", node: "heading", level: 3 },
  { label: "saved_tours.share.editor.h4", node: "heading", level: 4 },
]

// ---- small UI helpers ---------------------------------------------------

const ToolbarBtn = ({ onClick, active, disabled, title, children }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()} // keep editor selection on click
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`p-1.5 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
      active ? "bg-[#1F2937] text-white" : "text-[#4B5563] hover:bg-[#F4F6F9]"
    }`}
  >
    {children}
  </button>
)

const Divider = () => <div className="w-px h-5 bg-[#E7EAEF] mx-1" />

const Dropdown = ({ label, width = "w-40", align = "left", children }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener("mousedown", onDoc)
    return () => document.removeEventListener("mousedown", onDoc)
  }, [open])

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium text-[#1F2937]
                   hover:bg-[#F4F6F9] transition-colors"
      >
        {label}
        <ChevronDown size={12} className="text-[#9AA5B4]" />
      </button>
      {open && (
        <div
          className={`absolute z-10 top-full ${align === "right" ? "right-0" : "left-0"} mt-1 ${width} py-1 rounded-xl bg-white
                      border border-[#E7EAEF] shadow-lg max-h-64 overflow-y-auto`}
          onClick={() => setOpen(false)}
        >
          {children}
        </div>
      )}
    </div>
  )
}

const DropdownItem = ({ onClick, children }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    className="w-full text-left px-3 py-1.5 text-xs text-[#1F2937] hover:bg-[#F4F6F9] transition-colors"
  >
    {children}
  </button>
)

const GridPicker = ({ onPick, max = 6 }) => {
  const [hover, setHover] = useState({ r: 0, c: 0 })
  return (
    <div className="p-2">
      <div className="grid gap-0.5" style={{ gridTemplateColumns: `repeat(${max}, 14px)` }}>
        {Array.from({ length: max * max }).map((_, i) => {
          const r = Math.floor(i / max)
          const c = i % max
          const filled = r <= hover.r && c <= hover.c
          return (
            <div
              key={i}
              onMouseEnter={() => setHover({ r, c })}
              onClick={() => onPick(hover.r + 1, hover.c + 1)}
              className={`w-3.5 h-3.5 border rounded-sm cursor-pointer ${
                filled ? "bg-[#1F2937] border-[#1F2937]" : "border-[#E7EAEF]"
              }`}
            />
          )
        })}
      </div>
      <div className="text-[10px] text-[#9AA5B4] text-center mt-1.5">
        {hover.r + 1} × {hover.c + 1}
      </div>
    </div>
  )
}

// ---- preview: swap {{token}} with sample data in a rendered clone -------

const withSampleValues = (html) => {
  const el = document.createElement("div")
  el.innerHTML = html || ""
  const walk = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      node.textContent = VARIABLES.reduce(
        (acc, key) => acc.replaceAll(`{{${key}}}`, SAMPLE_VALUES[key]),
        node.textContent
      )
    } else {
      node.childNodes.forEach(walk)
    }
  }
  walk(el)
  return el.innerHTML
}

// ---- main component -------------------------------------------------

const RichTemplateEditor = ({ value, onChange, readOnly, expanded, onToggleExpanded }) => {
  const { t } = useTranslation()
  const [sourceMode, setSourceMode] = useState(false)
  const [sourceDraft, setSourceDraft] = useState(value || "")
  const [showPreview, setShowPreview] = useState(false)

  const editorMinHeight = expanded ? "60vh" : "320px"

  const editor = useEditor({
    editable: !readOnly,
    content: value || "",
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3, 4] } }),
      Underline,
      TextStyle,
      FontFamily,
      FontSize,
      LineHeight,
      Link.configure({ openOnClick: false, autolink: false }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      // real px/vh min-height on the actual contenteditable node — a % or h-full
      // here won't resolve, since the ancestor chain only has min-height (auto height),
      // and clicks below the last line would otherwise miss the editable area entirely
      attributes: { style: `min-height: ${editorMinHeight}; outline: none; cursor: text;` },
    },
  })

  useEffect(() => {
    if (editor && value !== editor.getHTML() && !sourceMode) {
      editor.commands.setContent(value || "", false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  // editorProps is only read once at creation — push height changes (fullscreen toggle) manually
  useEffect(() => {
    if (!editor) return
    editor.setOptions({
      editorProps: {
        attributes: { style: `min-height: ${editorMinHeight}; outline: none; cursor: text;` },
      },
    })
  }, [editor, editorMinHeight])

  if (!editor) return null

  const insertVariable = (key) => {
    editor
      .chain()
      .focus()
      .insertContent({
        type: "text",
        marks: [{ type: "textStyle", attrs: { fontSize: null } }],
        text: `{{${key}}}`,
      })
      .run()
  }

  const setHeading = (h) => {
    if (h.node === "paragraph") editor.chain().focus().setParagraph().run()
    else editor.chain().focus().toggleHeading({ level: h.level }).run()
  }

  const currentHeadingLabel = () => {
    const found = HEADINGS.find((h) =>
      h.node === "paragraph"
        ? editor.isActive("paragraph")
        : editor.isActive("heading", { level: h.level })
    )
    return t(found ? found.label : HEADINGS[0].label)
  }

  const setLink = () => {
    const prev = editor.getAttributes("link").href
    const url = window.prompt(t("saved_tours.share.editor.link_prompt"), prev || "https://")
    if (url === null) return
    if (url === "") {
      editor.chain().focus().unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run()
  }

  const toggleSource = () => {
    if (!sourceMode) {
      setSourceDraft(editor.getHTML())
      setSourceMode(true)
    } else {
      editor.commands.setContent(sourceDraft, false)
      onChange(sourceDraft)
      setSourceMode(false)
    }
  }

  return (
    <div className="border border-[#E7EAEF] rounded-xl bg-white flex flex-col">
      {/* Tailwind's Preflight resets ul/ol to list-style:none — without this, toggling
          bullet/ordered lists changes the underlying HTML but renders no visible marker/indent. */}
      <style>{`
        .tiptap-content ul { list-style: disc outside; padding-left: 1.5rem; margin: 0.4rem 0; }
        .tiptap-content ol { list-style: decimal outside; padding-left: 1.5rem; margin: 0.4rem 0; }
        .tiptap-content li { margin: 0.15rem 0; }
        .tiptap-content li p { margin: 0; }
        .tiptap-content li > ul, .tiptap-content li > ol { margin: 0.15rem 0; }
        .tiptap-content table { border-collapse: collapse; width: 100%; margin: 0.5rem 0; }
        .tiptap-content td, .tiptap-content th {
          border: 1px solid #E7EAEF; padding: 0.375rem 0.5rem; text-align: left;
        }
        .tiptap-content th { background: #F4F6F9; font-weight: 600; }
        .tiptap-content a { color: #2563EB; text-decoration: underline; }
        .tiptap-content .ProseMirror:focus { outline: none; }
      `}</style>
      {!readOnly && (
        <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 rounded-t-xl border-b border-[#E7EAEF] bg-[#FAFBFC]">
          <ToolbarBtn title="Undo" onClick={() => editor.chain().focus().undo().run()}>
            <Undo2 size={15} />
          </ToolbarBtn>
          <ToolbarBtn title="Redo" onClick={() => editor.chain().focus().redo().run()}>
            <Redo2 size={15} />
          </ToolbarBtn>

          <Divider />

          <Dropdown label={currentHeadingLabel()} width="w-36">
            {HEADINGS.map((h) => (
              <DropdownItem key={h.label} onClick={() => setHeading(h)}>
                {t(h.label)}
              </DropdownItem>
            ))}
          </Dropdown>

          <Dropdown label={editor.getAttributes("textStyle").fontFamily || "Inter"} width="w-36">
            {FONT_FAMILIES.map((f) => (
              <DropdownItem key={f} onClick={() => editor.chain().focus().setFontFamily(f).run()}>
                <span style={{ fontFamily: f }}>{f}</span>
              </DropdownItem>
            ))}
          </Dropdown>

          <Dropdown label={editor.getAttributes("textStyle").fontSize || "14px"} width="w-20">
            {FONT_SIZES.map((s) => (
              <DropdownItem key={s} onClick={() => editor.chain().focus().setFontSize(s).run()}>
                {s}
              </DropdownItem>
            ))}
          </Dropdown>

          <Divider />

          <ToolbarBtn
            title="Bold"
            active={editor.isActive("bold")}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <BoldIcon size={15} />
          </ToolbarBtn>
          <ToolbarBtn
            title="Italic"
            active={editor.isActive("italic")}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <ItalicIcon size={15} />
          </ToolbarBtn>
          <ToolbarBtn
            title="Underline"
            active={editor.isActive("underline")}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <UnderlineIcon size={15} />
          </ToolbarBtn>
          <ToolbarBtn
            title="Strikethrough"
            active={editor.isActive("strike")}
            onClick={() => editor.chain().focus().toggleStrike().run()}
          >
            <Strikethrough size={15} />
          </ToolbarBtn>
          <ToolbarBtn title="Link" active={editor.isActive("link")} onClick={setLink}>
            <LinkIcon size={15} />
          </ToolbarBtn>

          <Divider />

          <Dropdown label={<TableIcon size={15} />} width="w-auto" align="right">
            <GridPicker
              onPick={(rows, cols) =>
                editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run()
              }
            />
          </Dropdown>

          <Dropdown label={<Braces size={15} />} width="w-56" align="right">
            {VARIABLES.map((key) => (
              <DropdownItem key={key} onClick={() => insertVariable(key)}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono">{`{{${key}}}`}</span>
                  {TOKEN_LABELS[key] && (
                    <span className="text-[#9AA5B4] truncate">{TOKEN_LABELS[key]}</span>
                  )}
                </div>
              </DropdownItem>
            ))}
          </Dropdown>

          <ToolbarBtn
            title={t("saved_tours.share.editor.html_source")}
            active={sourceMode}
            onClick={toggleSource}
          >
            <Code2 size={15} />
          </ToolbarBtn>

          <Divider />

          <Dropdown
            label={
              editor.isActive({ textAlign: "center" }) ? (
                <AlignCenter size={15} />
              ) : editor.isActive({ textAlign: "right" }) ? (
                <AlignRight size={15} />
              ) : editor.isActive({ textAlign: "justify" }) ? (
                <AlignJustify size={15} />
              ) : (
                <AlignLeft size={15} />
              )
            }
            width="w-32"
            align="right"
          >
            <DropdownItem onClick={() => editor.chain().focus().setTextAlign("left").run()}>
              <AlignLeft size={13} className="inline mr-1.5" />{" "}
              {t("saved_tours.share.editor.align_left")}
            </DropdownItem>
            <DropdownItem onClick={() => editor.chain().focus().setTextAlign("center").run()}>
              <AlignCenter size={13} className="inline mr-1.5" />{" "}
              {t("saved_tours.share.editor.align_center")}
            </DropdownItem>
            <DropdownItem onClick={() => editor.chain().focus().setTextAlign("right").run()}>
              <AlignRight size={13} className="inline mr-1.5" />{" "}
              {t("saved_tours.share.editor.align_right")}
            </DropdownItem>
            <DropdownItem onClick={() => editor.chain().focus().setTextAlign("justify").run()}>
              <AlignJustify size={13} className="inline mr-1.5" />{" "}
              {t("saved_tours.share.editor.align_justify")}
            </DropdownItem>
          </Dropdown>

          <Dropdown
            label={
              <div className="flex items-center gap-0.5">
                <Indent size={13} />
                <ChevronDown size={10} />
              </div>
            }
            width="w-24"
            align="right"
          >
            {LINE_HEIGHTS.map((lh) => (
              <DropdownItem key={lh} onClick={() => editor.chain().focus().setLineHeight(lh).run()}>
                {lh}
              </DropdownItem>
            ))}
          </Dropdown>

          <ToolbarBtn
            title="Bullet list"
            active={editor.isActive("bulletList")}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <List size={15} />
          </ToolbarBtn>
          <ToolbarBtn
            title="Ordered list"
            active={editor.isActive("orderedList")}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <ListOrdered size={15} />
          </ToolbarBtn>
          <ToolbarBtn
            title="Indent"
            onClick={() => editor.chain().focus().sinkListItem("listItem").run()}
          >
            <Indent size={15} />
          </ToolbarBtn>
          <ToolbarBtn
            title="Outdent"
            onClick={() => editor.chain().focus().liftListItem("listItem").run()}
          >
            <Outdent size={15} />
          </ToolbarBtn>

          <Divider />

          <ToolbarBtn
            title={t("saved_tours.share.editor.clear_format")}
            onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
          >
            <RemoveFormatting size={15} />
          </ToolbarBtn>
          <ToolbarBtn
            title={t("saved_tours.share.editor.preview")}
            active={showPreview}
            onClick={() => setShowPreview((v) => !v)}
          >
            <span className="text-[10px] font-bold px-0.5">
              {t("saved_tours.share.editor.preview_short")}
            </span>
          </ToolbarBtn>
          <ToolbarBtn title="Fullscreen" onClick={onToggleExpanded}>
            {expanded ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
          </ToolbarBtn>
        </div>
      )}

      <div
        className={`rounded-b-xl overflow-y-auto cursor-text ${
          expanded ? "min-h-[60vh] max-h-[65vh]" : "min-h-[320px] max-h-[480px]"
        }`}
        onClick={() => !sourceMode && !showPreview && editor.chain().focus().run()}
      >
        {sourceMode ? (
          <textarea
            value={sourceDraft}
            onChange={(e) => setSourceDraft(e.target.value)}
            className="w-full h-full min-h-[inherit] p-4 font-mono text-xs text-[#1F2937] outline-none resize-none"
            spellCheck={false}
          />
        ) : showPreview ? (
          <div
            className="tiptap-content h-full px-4 py-3 text-sm text-[#1F2937]"
            dangerouslySetInnerHTML={{ __html: withSampleValues(editor.getHTML()) }}
          />
        ) : (
          <EditorContent
            editor={editor}
            className="tiptap-content h-full px-4 py-3 text-sm text-[#1F2937]"
          />
        )}
      </div>
    </div>
  )
}

export default RichTemplateEditor
